import {
  Sheet,
  SheetContent,
  SheetTitle,
} from "@/components/ui/sheet";
import { useState, useContext, useEffect } from "react";
import { useSearchParams } from "react-router-dom";
import MerchantContext from "../../../contexts/MerchantContext";
import { trpc } from "../../../trpc";
import ListingRequestListPanel from "./ListingRequestListPanel";
import ListingRequestDetailPanel from "./ListingRequestDetailPanel";

const ITEMS_PER_PAGE = 50;

const ListingRequestContent = () => {
  const { merchantId, country } = useContext(MerchantContext);
  const [searchParams, setSearchParams] = useSearchParams();
  const [selectedRequestId, setSelectedRequestId] = useState<string | undefined>(undefined);
  const [searchPhone, setSearchPhone] = useState<string>("");
  const [cursor, setCursor] = useState<string | undefined>(undefined);
  const [requestsData, setRequestsData] = useState<any[]>([]);
  const [fetchingData, setFetchingData] = useState<boolean>(true);

  // Handle URL query param on mount and when it changes
  const requestIdFromUrl = searchParams.get("id");

  useEffect(() => {
    if (requestIdFromUrl) {
      setSelectedRequestId(requestIdFromUrl);
    }
  }, [requestIdFromUrl]);

  useEffect(() => {
    if (merchantId) {
      setFetchingData(true);
    }
  }, [merchantId, cursor, searchPhone]);

  // Fetch listing requests list
  const { data: requestsResponse } = trpc.listingRequest.list.useQuery(
    {
      merchantId,
      limit: ITEMS_PER_PAGE,
      cursor,
      customerPhone: searchPhone || undefined,
    },
    {
      enabled: !!merchantId,
      refetchOnMount: "always",
      onSuccess: () => {
        setFetchingData(false);
      },
    }
  );

  // Update requestsData when query data changes
  useEffect(() => {
    if (requestsResponse?.data) {
      if (cursor) {
        // Load more: append to existing data
        setRequestsData((prev) => [...prev, ...requestsResponse.data]);
      } else {
        // First load or filter change: replace data
        setRequestsData(requestsResponse.data);
      }
    }
  }, [requestsResponse, cursor]);

  const hasMore = !!requestsResponse?.cursor;

  // Fetch selected request details
  const { data: requestDetail, isLoading: isLoadingDetail } = trpc.listingRequest.get.useQuery(
    {
      merchantId,
      id: selectedRequestId || "",
    },
    {
      enabled: !!merchantId && !!selectedRequestId,
    }
  );

  // Fetch lead data for the selected request
  const { data: leadData } = trpc.leads.get.useQuery(
    { merchantId, id: requestDetail?.leadId || "" },
    { enabled: !!merchantId && !!requestDetail?.leadId }
  );

  const handleRequestSelect = (id: string) => {
    setSelectedRequestId(id);
    setSearchParams({ id });
  };

  const handleLoadMore = () => {
    if (requestsResponse?.cursor) {
      setCursor(requestsResponse.cursor);
    }
  };

  const handleLeadUpdate = () => {
    // Optionally refetch or update data
  };

  // Reset cursor when search phone changes
  useEffect(() => {
    setCursor(undefined);
    setRequestsData([]);
  }, [searchPhone]);

  const handleDrawerClose = () => {
    setSelectedRequestId(undefined);
    setSearchParams({});
  };

  const isDrawerOpen = !!selectedRequestId;

  if (!merchantId) {
    return (
      <div className="flex h-[calc(100dvh-60px)] items-center justify-center md:h-screen">
        <p className="text-gray-400">No hay comercio seleccionado</p>
      </div>
    );
  }

  return (
    <div className="h-full overflow-hidden bg-[#F9F9FB]">
      {/* Full-width list of listing requests */}
      <ListingRequestListPanel
        requests={requestsData}
        selectedRequestId={selectedRequestId}
        onSelect={handleRequestSelect}
        searchPhone={searchPhone}
        onSearchChange={setSearchPhone}
        hasMore={hasMore}
        isLoading={fetchingData}
        onLoadMore={handleLoadMore}
        country={country}
      />

      {/* Slide-over drawer for request details and conversation */}
      <Sheet open={isDrawerOpen} onOpenChange={(open) => !open && handleDrawerClose()}>
        <SheetContent
          side="right"
          className="w-full p-0 sm:max-w-none md:w-[80%] lg:w-[70%]"
        >
          <SheetTitle className="sr-only">Detalle de solicitud de captación</SheetTitle>
          {isDrawerOpen && (
            <ListingRequestDetailPanel
              request={requestDetail}
              leadData={leadData}
              isLoading={isLoadingDetail}
              merchantId={merchantId}
              country={country}
              onLeadUpdate={handleLeadUpdate}
            />
          )}
        </SheetContent>
      </Sheet>
    </div>
  );
};

export default ListingRequestContent;
